"use client";

import { useRouter } from "next/navigation";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { BrandPulse } from "@/components/brand-pulse";
import { ErrorScreen } from "@/components/error-screen";
import { fetchDevices, type LinkedDevice } from "@/lib/console";

// Which linked device the console shows, remembered across visits. Written by
// select() here and by the link form (a just-linked device becomes current).
export const SELECTED_DEVICE_KEY = "firstmagic.selected-device";

interface DeviceContextValue {
  devices: LinkedDevice[]; // never empty — zero devices redirects to /link-device
  selected: LinkedDevice;
  select: (deviceId: string) => void;
  // Re-fetch after link/rename/unlink so every consumer sees the change.
  refresh: () => Promise<void>;
}

const DeviceContext = createContext<DeviceContextValue | null>(null);

export function useDevices(): DeviceContextValue {
  const value = useContext(DeviceContext);
  if (!value) throw new Error("useDevices must be used inside DeviceProvider");
  return value;
}

// Loads the parent's linked devices before the app frame renders, so pages
// can assume a current device the way they assume a session (AuthGuard).
// Zero linked devices funnels to the /link-device setup page instead of an
// empty dashboard.
export function DeviceProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [devices, setDevices] = useState<LinkedDevice[] | null>(null); // null = loading
  const [error, setError] = useState<Error | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      setDevices(await fetchDevices());
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
      // Rethrow so mutation flows that await refresh() (rename/unlink
      // dialogs) see the failed refetch instead of closing over stale data.
      throw cause;
    }
  }, []);

  useEffect(() => {
    // The error state already drives this first-load UI.
    refresh().catch(() => {});
  }, [refresh]);

  // localStorage only after mount — the static export prerenders without it.
  useEffect(() => {
    setSelectedId(localStorage.getItem(SELECTED_DEVICE_KEY));
  }, []);

  const noDevices = devices !== null && devices.length === 0;
  useEffect(() => {
    if (noDevices) router.replace("/link-device");
  }, [noDevices, router]);

  const select = useCallback((deviceId: string) => {
    localStorage.setItem(SELECTED_DEVICE_KEY, deviceId);
    setSelectedId(deviceId);
  }, []);

  // Memoized so renders above the provider (e.g. AuthGuard's window-focus
  // session refetch) don't hand every consumer a fresh context value.
  const value = useMemo<DeviceContextValue | null>(() => {
    if (devices === null || devices.length === 0) return null;
    // A stale stored id (e.g. the device was just unlinked) falls back to
    // the oldest link, matching the list's stable order.
    const selected = devices.find((d) => d.device_id === selectedId) ?? devices[0];
    return { devices, selected, select, refresh };
  }, [devices, selectedId, select, refresh]);

  // The full-screen error only before the first successful load — once a
  // device list exists, a failed background refresh keeps the (slightly
  // stale) console up instead of unmounting it under the parent.
  if (devices === null) {
    if (error) {
      return (
        <div className="h-dvh">
          <ErrorScreen error={error} retry={() => refresh().catch(() => {})} />
        </div>
      );
    }
    return <BrandPulse />;
  }
  if (noDevices || value === null) return <BrandPulse />;

  return <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>;
}
